import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import PropertyCard from '../components/PropertyCard';
import MapboxMap from '../components/MapboxMap';
import SEO from '../components/SEO';
import { PUERTO_RICO_CITIES } from '../utils/constants';
import { initializeFiltersFromURL, FOR_SALE_FILTER_KEYS } from '../utils/filterUtils';
import './PropertyList.css';

const LOCATION_LANDING_PAGES = {
  '/properties/for-sale/ponce': { city: 'Ponce', name: 'Ponce' },
  '/properties/for-sale/santa-isabel': { city: 'Santa Isabel', name: 'Santa Isabel' },
  '/properties/for-sale/guayama': { city: 'Guayama', name: 'Guayama' },
  '/properties/for-sale/lajas': { city: 'Lajas', name: 'Lajas' },
};

const ForSaleProperties = () => {
  const location = useLocation();
  const landingPage = LOCATION_LANDING_PAGES[location.pathname];
  const isCasasEnVentaPage = location.pathname === '/casas-en-venta';
  const isCasasReposeidasPage = location.pathname === '/casas-reposeidas';
  const isLandingPage = Boolean(landingPage || isCasasEnVentaPage || isCasasReposeidasPage);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize filters from URL query params
  const [filters, setFilters] = useState(() => 
    isLandingPage
      ? {
          ...initializeFiltersFromURL(location.search, FOR_SALE_FILTER_KEYS),
          ...(landingPage ? { city: landingPage.city } : {}),
          ...(isCasasReposeidasPage ? { isReposeida: 'true' } : {}),
        }
      : initializeFiltersFromURL(location.search, FOR_SALE_FILTER_KEYS)
  );
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Update filters when URL changes (for navigation between pages)
  useEffect(() => {
    const urlFilters = initializeFiltersFromURL(location.search, FOR_SALE_FILTER_KEYS);
    setFilters(isLandingPage
      ? {
          ...urlFilters,
          ...(landingPage ? { city: landingPage.city } : {}),
          ...(isCasasReposeidasPage ? { isReposeida: 'true' } : {}),
        }
      : urlFilters);
  }, [location.pathname, location.search, isLandingPage, landingPage, isCasasReposeidasPage]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('listingType', 'for-sale');
        params.append('page', pagination.page.toString());
        params.append('limit', '15');
        if (filters.status) params.append('status', filters.status);
        if (filters.propertyType) params.append('propertyType', filters.propertyType);
        if (filters.minPrice) params.append('minPrice', filters.minPrice);
        if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
        if (filters.isReposeida !== '') params.append('isReposeida', filters.isReposeida);
        if (filters.city) params.append('city', filters.city);

        const response = await axios.get(`/api/properties?${params.toString()}`);
        
        // Filter client-side for bedrooms and bathrooms
        let filteredProperties = response.data.properties || response.data;
        
        if (filters.bedrooms) {
          const minBedrooms = Number(filters.bedrooms);
          filteredProperties = filteredProperties.filter(prop => prop.bedrooms >= minBedrooms);
        }
        
        if (filters.bathrooms) {
          const minBathrooms = Number(filters.bathrooms);
          filteredProperties = filteredProperties.filter(prop => prop.bathrooms >= minBathrooms);
        }
        
        setProperties(filteredProperties);
        
        // Update pagination info if available in response
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [filters, pagination.page]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mapContainerStyle = {
    width: '100%',
    height: '600px',
  };

  // Find the first property with valid coordinates for map center
  const propertyWithCoordinates = properties.find(
    p => p.coordinates?.lat !== 0 && p.coordinates?.lng !== 0
  );

  const center = propertyWithCoordinates
    ? { lat: propertyWithCoordinates.coordinates.lat, lng: propertyWithCoordinates.coordinates.lng }
    : { lat: 18.2208, lng: -66.5901 };

  // Prepare markers for map view
  const mapMarkers = properties
    .filter(p => p.coordinates?.lat !== 0 && p.coordinates?.lng !== 0)
    .map(property => ({
      id: property.id,
      title: property.title,
      price: property.price,
      coordinates: property.coordinates
    }));

  return (
    <div className="property-list">
      <SEO 
        title={landingPage
          ? `Casas en Venta en ${landingPage.name}, Puerto Rico | Alicea Realty Group`
          : isCasasReposeidasPage
            ? "Casas Reposeídas en Puerto Rico | Alicea Realty Group"
            : isCasasEnVentaPage
              ? "Casas en Venta en Puerto Rico | Alicea Realty Group"
          : "Propiedades en Venta en Puerto Rico | Casas y Terrenos | Alicea Realty Group LLC | Noemi Alicea"}
        description={landingPage
          ? `Encuentre casas en venta en ${landingPage.name}, Puerto Rico. Explore propiedades disponibles, precios y detalles con la asesoría de Noemí Alicea González, Corredora Autorizada.`
          : isCasasReposeidasPage
            ? "Encuentre casas reposeídas y propiedades bancarias en Puerto Rico. Explore inventario disponible y reciba asesoría de Noemí Alicea González, Corredora Autorizada."
            : isCasasEnVentaPage
              ? "Encuentre casas en venta en Puerto Rico. Explore propiedades disponibles, precios y detalles con la asesoría de Noemí Alicea González."
          : "Explore propiedades en venta en Puerto Rico. Amplio catálogo de casas, terrenos y propiedades comerciales. Asesoría experta con Noemí Alicea González, Corredora Autorizada."}
        keywords={landingPage
          ? `casas en ${landingPage.name}, casas en venta en ${landingPage.name}, propiedades en venta ${landingPage.name} Puerto Rico, bienes raíces ${landingPage.name}, realtor ${landingPage.name}`
          : isCasasReposeidasPage
            ? "casas reposeídas Puerto Rico, casas reposeidas, propiedades reposeídas, propiedades bancarias Puerto Rico, HUD homes Puerto Rico, realtor Puerto Rico"
            : isCasasEnVentaPage
              ? "casas en venta, casas en ventas, casas en venta Puerto Rico, propiedades en venta Puerto Rico, bienes raíces Puerto Rico"
          : "propiedades en venta Puerto Rico, casas en venta Puerto Rico, terrenos en venta Puerto Rico, bienes raíces en venta Puerto Rico, comprar casa Puerto Rico, Alicea Realty Group, Noemi Alicea, San Juan, Bayamón, Caguas"}
        ogTitle={landingPage
          ? `Casas en Venta en ${landingPage.name}, Puerto Rico | Alicea Realty Group`
          : isCasasReposeidasPage
            ? "Casas Reposeídas en Puerto Rico | Alicea Realty Group"
            : isCasasEnVentaPage
              ? "Casas en Venta en Puerto Rico | Alicea Realty Group"
          : "Propiedades en Venta en Puerto Rico | Alicea Realty Group LLC"}
        ogDescription={landingPage
          ? `Explore casas y propiedades disponibles en venta en ${landingPage.name}, Puerto Rico.`
          : isCasasReposeidasPage
            ? "Explore casas reposeídas y propiedades bancarias disponibles en Puerto Rico."
            : isCasasEnVentaPage
              ? "Explore casas y propiedades disponibles en venta en Puerto Rico."
          : "Explore propiedades en venta en Puerto Rico. Amplio catálogo de casas, terrenos y propiedades comerciales."}
        canonical={`https://www.alicearealtypr.com${isLandingPage ? location.pathname : '/properties/for-sale'}`}
      />
      
      <div className="property-list-header">
        <div>
          <h1>
            {landingPage
              ? `Casas en Venta en ${landingPage.name}, Puerto Rico`
              : isCasasReposeidasPage
                ? 'Casas Reposeídas en Puerto Rico'
                : isCasasEnVentaPage
                  ? 'Casas en Venta en Puerto Rico'
                  : 'Propiedades en Venta'}
          </h1>
          {isLandingPage && (
            <p>
              {isCasasReposeidasPage
                ? 'Explore nuestro inventario de casas reposeídas y propiedades bancarias disponibles. Compare precios y características, y contacte a Alicea Realty Group para recibir orientación.'
                : `Explore nuestro inventario de casas y propiedades en venta${landingPage ? ` en ${landingPage.name}` : ' en Puerto Rico'}. Compare precios, ubicación y características, y contacte a Alicea Realty Group para recibir orientación.`}
            </p>
          )}
        </div>
        <div className="view-toggle">
          <button 
            className={viewMode === 'grid' ? 'active' : ''} 
            onClick={() => setViewMode('grid')}
          >
            Vista de Cuadrícula
          </button>
          <button 
            className={viewMode === 'map' ? 'active' : ''} 
            onClick={() => setViewMode('map')}
          >
            Vista de Mapa
          </button>
        </div>
      </div>

      <div className="filters">
        <select name="status" value={filters.status} onChange={handleFilterChange}>
          <option value="">Todos los Estados</option>
          <option value="available">Disponible</option>
          <option value="pending">Opcionada</option>
        </select>

        <select name="propertyType" value={filters.propertyType} onChange={handleFilterChange}>
          <option value="">Todos los Tipos</option>
          <option value="house">Casa</option>
          <option value="apartment">Apartamento</option>
          <option value="condo">Condominio</option>
          <option value="walkup">Walk Up</option>
          <option value="multifamiliar">Multifamiliar</option>
          <option value="finca">Finca</option>
          <option value="duplex">Duplex</option>
          <option value="land">Terreno</option>
          <option value="commercial">Comercial</option>
        </select>

        <select name="city" value={filters.city} onChange={handleFilterChange}>
          <option value="">Todas las Ciudades</option>
          {PUERTO_RICO_CITIES.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>

        <select name="bedrooms" value={filters.bedrooms} onChange={handleFilterChange}>
          <option value="">Todas las Habitaciones</option>
          <option value="1">1+</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
          <option value="5">5+</option>
        </select>

        <select name="bathrooms" value={filters.bathrooms} onChange={handleFilterChange}>
          <option value="">Todos los Baños</option>
          <option value="1">1+</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
        </select>

        <select name="isReposeida" value={filters.isReposeida} onChange={handleFilterChange}>
          <option value="">Todas (Reposeidas y No Reposeidas)</option>
          <option value="true">Solo Reposeidas</option>
          <option value="false">No Reposeidas</option>
        </select>

        <input
          type="number"
          name="minPrice"
          placeholder="Precio Mínimo"
          value={filters.minPrice}
          onChange={handleFilterChange}
        />

        <input
          type="number"
          name="maxPrice"
          placeholder="Precio Máximo"
          value={filters.maxPrice}
          onChange={handleFilterChange}
        />
      </div>

      {loading ? (
        <div className="loading">Cargando propiedades...</div>
      ) : viewMode === 'grid' ? (
        <>
          <div className="properties-grid">
            {properties.length > 0 ? (
              properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))
            ) : (
              <p className="no-properties">No se encontraron propiedades en venta</p>
            )}
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="pagination-btn"
              >
                Anterior
              </button>
              <span className="pagination-info">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} propiedades)
              </span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="pagination-btn"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="map-view">
          <MapboxMap
            center={center}
            markers={mapMarkers}
            style={mapContainerStyle}
            zoom={12}
          />
        </div>
      )}
    </div>
  );
};

export default ForSaleProperties;
